import { useEffect, useState } from 'react'
import { useFeatures } from 'flagged'
import { truncate } from 'lodash'
import { RegistryProfileIcon } from 'components/icons'
import Image from 'components/image'
import { ChipVariants } from 'components/Chip'
import { DropdownButton } from 'components/DropdownButton/DropdownButton'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import GhostLine from 'shared/GhostLine/GhostLine'
import { AccountMenuItem } from './AccountMenuItem'
import { track, useTracking } from '../../../lib/analytics'
import * as urls from '../../../lib/urls'
import css from '../global-nav.scss'
import { UPDATE_NAVBAR_USER_ICON } from '../../../registry/constants'

interface UserProps {
  addedBabylistButton: boolean
  currentRegistry: {
    id: number
    ownerId: number
    photoUrl?: string
    urlSlug: string
  }
  firstName: string
  hasMultipleRegistries: boolean
  id: number
}

const AccountMenuItems = ({
  currentUser,
  trackNavbarClicked,
}: {
  currentUser?: UserProps
  trackNavbarClicked: (eventCta: string) => void
}) => {
  const { trackEvent } = useTracking()
  const features = useFeatures()
  const { currentRegistry, addedBabylistButton, hasMultipleRegistries } =
    currentUser || {}

  const handleCreateNewRegistryClicked = () => {
    trackEvent({
      event: track.createNewRegistryClicked,
      eventLocation: track.EventLocation.SETTINGS,
    })
    window.location.href = urls.createAnotherRegistryPath
  }

  const handleListTypeClicked =
    (listType: string, redirectPath: string) => () => {
      trackEvent({
        event: track.listTypeClicked,
        eventLocation: track.EventLocation.SETTINGS,
        listType,
        registryId: currentRegistry?.id,
        registryOwnerId: currentRegistry?.ownerId,
      })
      window.location.href = redirectPath
    }

  const trackLogOut = (e: any) => {
    e.preventDefault()
    trackEvent({ event: track.logOutCompleted })
    window.location.href = urls.logoutPath
  }

  const links = [
    {
      label: 'Your Registry',
      href: urls.registryPath(currentRegistry?.urlSlug),
    },
    !addedBabylistButton && {
      label: 'Install Babylist Button',
      href: urls.addItemsPath,
    },
    {
      label: 'Settings',
      href: urls.settingsPath,
    },
    {
      label: 'Your Orders',
      href: urls.settingsYourOrdersPath,
    },
    {
      label: 'Help',
      href: urls.faqPath,
      onClick: () => trackNavbarClicked('Help'),
    },
    {
      type: 'divider',
    },
    {
      label: 'New Registry',
      onClick: handleCreateNewRegistryClicked,
    },
    {
      label: 'New Birthday Wishlist',
      onClick: handleListTypeClicked(
        track.ListType.BIRTHDAY,
        urls.createBirthdayWishlistPath
      ),
    },
    features?.holiday_lists_discoverable && {
      label: 'New Holiday Wishlist',
      onClick: handleListTypeClicked(
        track.ListType.HOLIDAY,
        urls.createHolidayWishlistPath
      ),
      chipLabel: 'New!',
      chipVariant: ChipVariants.SUCCESS,
    },
    hasMultipleRegistries && {
      label: 'Switch Registry',
      href: urls.listsPath,
    },
    {
      label: 'Log Out',
      href: urls.logoutPath,
      onClick: trackLogOut,
    },
  ].filter(Boolean) as {
    label: string
    href?: string
    onClick?: () => void
    chipLabel?: string
    // eslint-disable-next-line prettier/prettier
    chipVariant?: (typeof ChipVariants)[keyof typeof ChipVariants]
  }[]

  return links
}

export default function AccountMenu({
  trackNavbarClicked,
}: {
  trackNavbarClicked: (eventCta: string) => void
}) {
  const [currentUser] = useCurrentUser()
  const { currentRegistry, id, firstName } = currentUser || {}

  const [photoUrl, setPhotoUrl] = useState(currentRegistry?.photoUrl)

  useEffect(() => {
    setPhotoUrl(currentRegistry?.photoUrl)
  }, [currentRegistry?.photoUrl])

  useEffect(() => {
    const updatePhotoUrl = ({ newUrl }: { newUrl: string }) =>
      setPhotoUrl(newUrl)
    PubSub.subscribe(UPDATE_NAVBAR_USER_ICON, updatePhotoUrl)
    return () => PubSub.unsubscribe(UPDATE_NAVBAR_USER_ICON, updatePhotoUrl)
  }, [])

  const avatar = photoUrl ? (
    <Image
      className="mrs circle-mask"
      height={34}
      lazyLoad={false}
      role="presentation"
      src={photoUrl}
      width={34}
    />
  ) : (
    <span aria-label="Baby Icon" className="mrs circle-mask bas pas" role="img">
      <RegistryProfileIcon height={20} width={20} />
    </span>
  )
  const title = (
    <div className="flex-row pull-left">
      {avatar}
      <span className={css.userNameWrap}>
        {firstName === undefined && (
          <GhostLine className={css.userNameLoadingState} />
        )}
        {firstName && truncate(firstName, { length: 13 })}
      </span>
    </div>
  )

  return (
    <DropdownButton
      aria-label="my account"
      bsStyle="nav-link"
      className="hidden-xs"
      id="account-menu"
      key={id}
      title={title}
    >
      {AccountMenuItems({
        currentUser,
        trackNavbarClicked,
      }).map((item, index) => (
        <AccountMenuItem eventKey={index} key={item.label} {...item} />
      ))}
    </DropdownButton>
  )
}
